import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PhotoCamera, Save, Cancel, ArrowBack } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import SettingsSidebar from '../../components/Common/SettingsSidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
}

const AddUser: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role_id: '',
    status: 'active',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  // Trigger email validation when email field changes (including auto-fill)
  useEffect(() => {
    if (formData.email && /\S+@\S+\.\S+/.test(formData.email)) {
      // Clear previous timeout
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
      
      // Set new timeout for validation
      emailTimeoutRef.current = setTimeout(() => {
        validateEmailRealTime(formData.email);
      }, 800);
    }
  }, [formData.email]);

  const fetchRoles = async () => {
    try {
      const response = await apiService.getRoles();
      setRoles(response.data.data || response.data);
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      toast.error(error.message || 'Failed to fetch roles');
    }
  };

  const validateEmailRealTime = async (email: string) => {
    console.log('validateEmailRealTime called with:', email);
    
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      console.log('Email validation skipped - invalid format');
      return;
    }

    try {
      console.log('Making API call to validate email...');
      const response = await apiService.validateEmail(email);
      console.log('Email validation response:', response);
      
      if (response.data && !response.data.available) {
        console.log('Setting error message:', response.data.message);
        setErrors(prev => ({ ...prev, email: response.data.message }));
      } else {
        console.log('Clearing error message');
        setErrors(prev => ({ ...prev, email: '' }));
      }
    } catch (error: any) {
      console.error('Email validation error:', error);
      // Handle validation errors from the API
      if (error.response && error.response.data && error.response.data.message) {
        console.log('Setting error from catch block:', error.response.data.message);
        setErrors(prev => ({ ...prev, email: error.response.data.message }));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Real-time email validation
    if (name === 'email' && value) {
      // Clear previous timeout
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
      
      // Set new timeout
      emailTimeoutRef.current = setTimeout(() => {
        validateEmailRealTime(value);
      }, 800);
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user selects
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.role_id) {
      newErrors.role_id = 'Please select a role';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        role_id: parseInt(formData.role_id),
        status: formData.status,
      };

      await apiService.createUser(userData);
      toast.success('User created successfully');
      
      // Redirect to users list after successful creation
      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      // Handle validation errors from the API
      if (error.response && error.response.data && error.response.data.errors) {
        const apiErrors = error.response.data.errors;
        const newErrors: Record<string, string> = {};
        
        // Map API errors to form errors
        if (apiErrors.email) {
          newErrors.email = apiErrors.email[0];
        }
        if (apiErrors.name) {
          newErrors.name = apiErrors.name[0];
        }
        if (apiErrors.phone) {
          newErrors.phone = apiErrors.phone[0];
        }
        if (apiErrors.password) {
          newErrors.password = apiErrors.password[0];
        }
        if (apiErrors.role_id) {
          newErrors.role_id = apiErrors.role_id[0];
        }
        
        setErrors(newErrors);
      } else {
        toast.error(error.message || 'Failed to create user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Add New User
        </Typography>
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            borderColor: '#e5e7eb',
            backgroundColor: 'white',
            color: '#374151',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              borderColor: '#d1d5db',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Cancel
        </Button>
      </Box>

     

      <Grid container spacing={3}>
        {/* Left Sidebar - Settings Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: 'calc(100vh - 160px)' }}>
            <CardContent sx={{ p: 3, height: '100%', overflow: 'auto' }}>
              {/* Form Header */}
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
                Add New User
              </Typography>

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Basic Information */}
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                        Basic Information
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            name="name"
                            label="Full Name"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            name="email"
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            name="phone"
                            label="Phone Number"
                            value={formData.phone}
                            onChange={handleInputChange}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <FormControl fullWidth error={!!errors.role_id}>
                            <InputLabel>Role</InputLabel>
                            <Select
                              name="role_id"
                              value={formData.role_id}
                              onChange={handleSelectChange}
                              label="Role"
                              sx={{
                                borderRadius: '8px',
                              }}
                            >
                              {roles.map((role) => (
                                <MenuItem key={role.id} value={role.id}>
                                  {role.display_name || role.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.role_id && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                                {errors.role_id}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                              name="status"
                              value={formData.status}
                              onChange={handleSelectChange}
                              label="Status"
                              sx={{
                                borderRadius: '8px',
                              }}
                            >
                              <MenuItem value="active">Active</MenuItem>
                              <MenuItem value="inactive">Inactive</MenuItem>
                              <MenuItem value="suspended">Suspended</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Account Information */}
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                        Account Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            name="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>


                </Grid>

                {/* Form Footer */}
                <Box sx={{ 
                  mt: 4, 
                  pt: 3, 
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#f8fafc',
                  borderRadius: '0 0 12px 12px',
                  margin: '0 -24px -24px -24px',
                  padding: '24px'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      onClick={handleSubmit}
                      sx={{
                        px: 4,
                        py: 2,
                        borderRadius: '8px',
                        backgroundColor: '#4A90E2',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1rem',
                        textTransform: 'none',
                        minWidth: '140px',
                        '&:hover': {
                          backgroundColor: '#3b82f6',
                          boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
                        },
                        '&:disabled': {
                          backgroundColor: '#9ca3af',
                          color: 'white',
                        },
                      }}
                    >
                      {loading ? 'Creating...' : 'Create User'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
};

export default AddUser;